import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from './AuthContext';

const GameContext = createContext();

export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}

const generateRewardCode = () => {
  const prefix = '91PLAY';
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${prefix}-${random}`;
};

export function GameProvider({ children }) {
  const { user, updateUser } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [dailyChallenges, setDailyChallenges] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeData = async () => {
      try {
        // Load saved data from localStorage
        const savedQuizzes = localStorage.getItem('quizzes');
        const savedLeaderboard = localStorage.getItem('leaderboard');
        const savedDailyChallenges = localStorage.getItem('dailyChallenges');
        const savedAchievements = localStorage.getItem('achievements');
        const savedTournaments = localStorage.getItem('tournaments');

        if (savedQuizzes) {
          const parsedQuizzes = JSON.parse(savedQuizzes);
          if (Array.isArray(parsedQuizzes) && parsedQuizzes.length > 0) {
            setQuizzes(parsedQuizzes);
          } else {
            await initializeQuizzes();
          }
        } else {
          await initializeQuizzes();
        }

        if (savedLeaderboard) {
          setLeaderboard(JSON.parse(savedLeaderboard));
        } else {
          initializeLeaderboard();
        }

        if (savedDailyChallenges) {
          setDailyChallenges(JSON.parse(savedDailyChallenges));
        } else {
          initializeDailyChallenges();
        }

        if (savedAchievements) {
          setAchievements(JSON.parse(savedAchievements));
        } else {
          initializeAchievements();
        }

        if (savedTournaments) {
          setTournaments(JSON.parse(savedTournaments));
        } else {
          initializeTournaments();
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing game data:', error);
        // If there's an error, try to initialize everything from scratch
        try {
          await initializeQuizzes();
          initializeLeaderboard();
          initializeDailyChallenges();
          initializeAchievements();
          initializeTournaments();
        } catch (initError) {
          console.error('Error during fallback initialization:', initError);
        } finally {
          setIsInitialized(true);
        }
      }
    };

    initializeData();
  }, []);

  const initializeQuizzes = async () => {
    try {
      // Initialize quizzes
      const initialQuizzes = [
        {
          id: 1,
          title: 'General Knowledge',
          questions: [
            {
              id: 1,
              question: 'What is the capital of France?',
              options: ['London', 'Berlin', 'Paris', 'Madrid'],
              correctAnswer: 'Paris',
              points: 10
            },
            {
              id: 2,
              question: 'Which planet is known as the Red Planet?',
              options: ['Venus', 'Mars', 'Jupiter', 'Saturn'],
              correctAnswer: 'Mars',
              points: 10
            },
            {
              id: 3,
              question: 'What is the largest mammal in the world?',
              options: ['Elephant', 'Blue Whale', 'Giraffe', 'Hippopotamus'],
              correctAnswer: 'Blue Whale',
              points: 10
            },
            {
              id: 4,
              question: 'Who painted the Mona Lisa?',
              options: ['Vincent van Gogh', 'Pablo Picasso', 'Leonardo da Vinci', 'Michelangelo'],
              correctAnswer: 'Leonardo da Vinci',
              points: 10
            },
            {
              id: 5,
              question: 'What is the chemical symbol for gold?',
              options: ['Ag', 'Fe', 'Au', 'Cu'],
              correctAnswer: 'Au',
              points: 10
            },
            {
              id: 6,
              question: 'Which country is home to the kangaroo?',
              options: ['New Zealand', 'South Africa', 'Australia', 'Brazil'],
              correctAnswer: 'Australia',
              points: 10
            },
            {
              id: 7,
              question: 'What is the largest ocean on Earth?',
              options: ['Atlantic Ocean', 'Indian Ocean', 'Arctic Ocean', 'Pacific Ocean'],
              correctAnswer: 'Pacific Ocean',
              points: 10
            },
            {
              id: 8,
              question: 'Who wrote "Romeo and Juliet"?',
              options: ['Charles Dickens', 'William Shakespeare', 'Jane Austen', 'Mark Twain'],
              correctAnswer: 'William Shakespeare',
              points: 10
            },
            {
              id: 9,
              question: 'What is the tallest mountain in the world?',
              options: ['K2', 'Mount Kilimanjaro', 'Mount Everest', 'Mount Fuji'],
              correctAnswer: 'Mount Everest',
              points: 10
            },
            {
              id: 10,
              question: 'Which element has the atomic number 1?',
              options: ['Helium', 'Oxygen', 'Hydrogen', 'Carbon'],
              correctAnswer: 'Hydrogen',
              points: 10
            }
          ]
        },
        {
          id: 2,
          title: 'Science Quiz',
          questions: [
            {
              id: 1,
              question: 'What is the chemical formula for water?',
              options: ['H2O', 'CO2', 'O2', 'H2SO4'],
              correctAnswer: 'H2O',
              points: 10
            },
            {
              id: 2,
              question: 'What is the speed of light?',
              options: ['300,000 km/s', '150,000 km/s', '450,000 km/s', '600,000 km/s'],
              correctAnswer: '300,000 km/s',
              points: 10
            },
            {
              id: 3,
              question: 'What is the largest organ in the human body?',
              options: ['Heart', 'Liver', 'Skin', 'Brain'],
              correctAnswer: 'Skin',
              points: 10
            },
            {
              id: 4,
              question: 'What is the main gas that makes up the Earth\'s atmosphere?',
              options: ['Oxygen', 'Carbon Dioxide', 'Nitrogen', 'Helium'],
              correctAnswer: 'Nitrogen',
              points: 10
            },
            {
              id: 5,
              question: 'What is the process by which plants make their food?',
              options: ['Respiration', 'Photosynthesis', 'Transpiration', 'Digestion'],
              correctAnswer: 'Photosynthesis',
              points: 10
            },
            {
              id: 6,
              question: 'What is the unit of electrical resistance?',
              options: ['Volt', 'Ampere', 'Ohm', 'Watt'],
              correctAnswer: 'Ohm',
              points: 10
            },
            {
              id: 7,
              question: 'Which planet has the most moons?',
              options: ['Jupiter', 'Saturn', 'Uranus', 'Neptune'],
              correctAnswer: 'Saturn',
              points: 10
            },
            {
              id: 8,
              question: 'What is the pH value of pure water?',
              options: ['5', '7', '9', '11'],
              correctAnswer: '7',
              points: 10
            },
            {
              id: 9,
              question: 'What is the study of fossils called?',
              options: ['Paleontology', 'Archaeology', 'Geology', 'Biology'],
              correctAnswer: 'Paleontology',
              points: 10
            },
            {
              id: 10,
              question: 'Which gas is responsible for the greenhouse effect?',
              options: ['Oxygen', 'Nitrogen', 'Carbon Dioxide', 'Helium'],
              correctAnswer: 'Carbon Dioxide',
              points: 10
            }
          ]
        },
        {
          id: 3,
          title: 'History Quiz',
          questions: [
            {
              id: 1,
              question: 'Who was the first President of the United States?',
              options: ['Thomas Jefferson', 'George Washington', 'Abraham Lincoln', 'John Adams'],
              correctAnswer: 'George Washington',
              points: 10
            },
            {
              id: 2,
              question: 'In which year did World War II end?',
              options: ['1943', '1944', '1945', '1946'],
              correctAnswer: '1945',
              points: 10
            },
            {
              id: 3,
              question: 'Who discovered America?',
              options: ['Christopher Columbus', 'Vasco da Gama', 'Ferdinand Magellan', 'Marco Polo'],
              correctAnswer: 'Christopher Columbus',
              points: 10
            },
            {
              id: 4,
              question: 'Which ancient civilization built the pyramids?',
              options: ['Greeks', 'Romans', 'Egyptians', 'Mayans'],
              correctAnswer: 'Egyptians',
              points: 10
            },
            {
              id: 5,
              question: 'Who was the first man to walk on the moon?',
              options: ['Neil Armstrong', 'Buzz Aldrin', 'Yuri Gagarin', 'John Glenn'],
              correctAnswer: 'Neil Armstrong',
              points: 10
            },
            {
              id: 6,
              question: 'In which year did the Titanic sink?',
              options: ['1910', '1912', '1914', '1916'],
              correctAnswer: '1912',
              points: 10
            },
            {
              id: 7,
              question: 'Who was the first female Prime Minister of the United Kingdom?',
              options: ['Margaret Thatcher', 'Theresa May', 'Indira Gandhi', 'Golda Meir'],
              correctAnswer: 'Margaret Thatcher',
              points: 10
            },
            {
              id: 8,
              question: 'Which country was the first to give women the right to vote?',
              options: ['United States', 'United Kingdom', 'New Zealand', 'France'],
              correctAnswer: 'New Zealand',
              points: 10
            },
            {
              id: 9,
              question: 'Who was the first Emperor of Rome?',
              options: ['Julius Caesar', 'Augustus', 'Nero', 'Constantine'],
              correctAnswer: 'Augustus',
              points: 10
            },
            {
              id: 10,
              question: 'In which year did the Berlin Wall fall?',
              options: ['1987', '1989', '1991', '1993'],
              correctAnswer: '1989',
              points: 10
            }
          ]
        }
      ];

      setQuizzes(initialQuizzes);
      localStorage.setItem('quizzes', JSON.stringify(initialQuizzes));
    } catch (error) {
      console.error('Error initializing quizzes:', error);
      throw error;
    }
  };

  const initializeLeaderboard = () => {
    const mockLeaderboard = [
      { username: 'Champion123', score: 1200, level: 10, avatar: '👑' },
      { username: 'QuizMaster', score: 980, level: 8, avatar: '🎯' },
      { username: 'GamePro', score: 850, level: 7, avatar: '🎮' }
    ];
    setLeaderboard(mockLeaderboard);
    localStorage.setItem('leaderboard', JSON.stringify(mockLeaderboard));
  };

  const initializeDailyChallenges = () => {
    const challenges = [
      {
        id: 1,
        title: 'Complete 3 Quizzes',
        description: 'Test your knowledge and complete 3 quizzes',
        reward: 50,
        progress: 0,
        target: 3,
        type: 'quiz'
      },
      {
        id: 2,
        title: 'Spin the Wheel 5 Times',
        description: 'Try your luck and spin the wheel 5 times',
        reward: 100,
        progress: 0,
        target: 5,
        type: 'spin'
      },
      {
        id: 3,
        title: 'Join a Tournament',
        description: 'Participate in a tournament',
        reward: 200,
        progress: 0,
        target: 1,
        type: 'tournament'
      }
    ];
    setDailyChallenges(challenges);
    localStorage.setItem('dailyChallenges', JSON.stringify(challenges));
  };

  const initializeAchievements = () => {
    const initialAchievements = [
      {
        id: 1,
        title: 'Quiz Master',
        description: 'Complete 10 quizzes with perfect scores',
        reward: 500,
        progress: 0,
        target: 10,
        type: 'quiz',
        icon: '🏆'
      },
      {
        id: 2,
        title: 'Lucky Spinner',
        description: 'Win 1000 coins from the spin wheel',
        reward: 1000,
        progress: 0,
        target: 1000,
        type: 'spin',
        icon: '🎰'
      },
      {
        id: 3,
        title: 'Tournament Champion',
        description: 'Win 3 tournaments',
        reward: 2000,
        progress: 0,
        target: 3,
        type: 'tournament',
        icon: '🏅'
      }
    ];
    setAchievements(initialAchievements);
    localStorage.setItem('achievements', JSON.stringify(initialAchievements));
  };

  const initializeTournaments = () => {
    const initialTournaments = [
      {
        id: 1,
        title: 'Weekly Quiz Challenge',
        description: 'Test your knowledge and win big prizes!',
        entryFee: 100,
        prizePool: 1000,
        participants: [],
        status: 'active',
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: 2,
        title: 'Spin Master Tournament',
        description: 'Compete in the ultimate spin wheel challenge!',
        entryFee: 200,
        prizePool: 2000,
        participants: [],
        status: 'upcoming',
        endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()
      }
    ];
    setTournaments(initialTournaments);
    localStorage.setItem('tournaments', JSON.stringify(initialTournaments));
  };

  const generateReward = async (type, amount) => {
    try {
      const newReward = {
        id: Date.now(),
        type,
        amount,
        date: new Date().toISOString(),
        status: 'pending'
      };

      setRewards(prev => [...prev, newReward]);
      await updateUser({ coins: user.coins + amount });
      return { success: true, reward: newReward };
    } catch (error) {
      console.error('Error generating reward:', error);
      return { success: false, error: error.message };
    }
  };

  const claimReward = async (rewardId) => {
    try {
      setRewards(prev =>
        prev.map(reward =>
          reward.id === rewardId ? { ...reward, status: 'claimed' } : reward
        )
      );
      return { success: true };
    } catch (error) {
      console.error('Error claiming reward:', error);
      return { success: false, error: error.message };
    }
  };

  const submitQuizAnswer = async (quizId, questionId, answer) => {
    try {
      const quiz = quizzes.find(q => q.id === quizId);
      const question = quiz.questions.find(q => q.id === questionId);
      const isCorrect = question.correctAnswer === answer;

      if (isCorrect) {
        await generateReward('quiz', question.points);
      }

      return { success: true, isCorrect, points: isCorrect ? question.points : 0 };
    } catch (error) {
      console.error('Error submitting quiz answer:', error);
      return { success: false, error: error.message };
    }
  };

  const spinWheel = () => {
    const rewards = [
      { type: 'coins', amount: 10 },
      { type: 'coins', amount: 20 },
      { type: 'coins', amount: 50 },
      { type: 'coins', amount: 100 },
      { type: 'bonus', amount: 5 },
      { type: 'bonus', amount: 10 }
    ];

    const randomIndex = Math.floor(Math.random() * rewards.length);
    const reward = rewards[randomIndex];
    const generatedReward = generateReward(reward.type, reward.amount);

    if (user) {
      updateUser({
        rewards: [...(user.rewards || []), generatedReward]
      });
    }

    return generatedReward;
  };

  const joinTournament = async (tournamentId) => {
    try {
      const tournament = tournaments.find(t => t.id === tournamentId);
      
      if (!tournament) {
        return { success: false, message: 'Tournament not found' };
      }

      if (tournament.status !== 'active') {
        return { success: false, message: 'Tournament is not active' };
      }

      if (tournament.participants.includes(user.id)) {
        return { success: false, message: 'You have already joined this tournament' };
      }

      if (user.coins < tournament.entryFee) {
        return { success: false, message: 'Insufficient coins to join tournament' };
      }

      // Deduct entry fee
      await updateUser({ coins: user.coins - tournament.entryFee });

      // Add user to participants
      setTournaments(prev =>
        prev.map(t =>
          t.id === tournamentId
            ? { ...t, participants: [...t.participants, user.id] }
            : t
        )
      );

      return { success: true, message: 'Successfully joined the tournament' };
    } catch (error) {
      console.error('Error joining tournament:', error);
      return { success: false, message: error.message };
    }
  };

  const updateLeaderboard = async (userId, score) => {
    try {
      setLeaderboard(prev => {
        const existingEntry = prev.find(entry => entry.userId === userId);
        if (existingEntry) {
          return prev.map(entry =>
            entry.userId === userId
              ? { ...entry, score: entry.score + score }
              : entry
          );
        }
        return [...prev, { userId, score }];
      });
    } catch (error) {
      console.error('Error updating leaderboard:', error);
    }
  };

  const checkDailyReward = async () => {
    try {
      const lastLogin = localStorage.getItem('lastLogin');
      const today = new Date().toDateString();
      
      if (lastLogin !== today) {
        // Generate daily reward
        const rewardAmount = 50; // Base reward
        const streak = parseInt(localStorage.getItem('loginStreak') || '0');
        const bonus = streak * 10; // 10 coins bonus per day of streak
        
        const totalReward = rewardAmount + bonus;
        
        // Update streak
        localStorage.setItem('loginStreak', (streak + 1).toString());
        localStorage.setItem('lastLogin', today);
        
        // Add reward
        await generateReward('daily', totalReward);
        
        return {
          success: true,
          amount: totalReward,
          streak: streak + 1
        };
      }
      
      return {
        success: false,
        message: 'Already claimed today\'s reward'
      };
    } catch (error) {
      console.error('Error checking daily reward:', error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  const value = {
    quizzes,
    rewards,
    leaderboard,
    dailyChallenges,
    achievements,
    tournaments,
    isInitialized,
    generateReward,
    claimReward,
    submitQuizAnswer,
    spinWheel,
    joinTournament,
    updateLeaderboard,
    checkDailyReward,
    verifyAndInitializeQuizzes: async () => {
      try {
        if (!quizzes || !Array.isArray(quizzes) || quizzes.length === 0) {
          await initializeQuizzes();
        }
        return true;
      } catch (error) {
        console.error('Error verifying quizzes:', error);
        return false;
      }
    }
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
} 